import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, LogOut, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const inputCls =
  "w-full border border-line bg-coal-900 px-4 py-3.5 text-sm text-white placeholder:text-steel/50 outline-none transition-colors duration-300 focus:border-ember rounded-none";

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  const loadQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch {
      setQuotes([]);
    } finally {
      setLoadingQuotes(false);
    }
  };

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => { setUser(data); loadQuotes(); })
      .catch(() => setUser(null));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      loadQuotes();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setQuotes([]);
  };

  const toggleStatus = async (quote) => {
    const next = quote.status === "new" ? "contacted" : "new";
    setQuotes((qs) => qs.map((q) => (q.id === quote.id ? { ...q, status: next } : q)));
    try {
      await api.patch(`/admin/quotes/${quote.id}`, { status: next });
    } catch {
      setQuotes((qs) => qs.map((q) => (q.id === quote.id ? { ...q, status: quote.status } : q)));
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-coal-900 text-white">
      <header className="flex items-center justify-between border-b border-line px-5 py-4 md:px-10">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center bg-ember font-display text-xs text-coal-900">MG</span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          {user && (
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel transition-colors hover:text-ember"
            >
              <LogOut size={13} /> Logout
            </button>
          )}
          <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel transition-colors hover:text-ember">
            <ArrowLeft size={13} /> Site
          </Link>
        </div>
      </header>

      {user === undefined ? (
        <div className="flex justify-center py-32"><Loader2 className="animate-spin text-ember" /></div>
      ) : !user ? (
        <div className="mx-auto max-w-md px-5 py-24 md:py-32">
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ember">[ Restricted ]</p>
          <h1 className="mb-10 font-display text-5xl uppercase leading-[0.85] tracking-tight">Sign in</h1>
          <form data-testid="admin-login-form" onSubmit={login} className="space-y-5 border border-line bg-coal-800 p-6 md:p-8">
            <div>
              <label htmlFor="admin-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@…" className={inputCls} />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="border border-ember/50 bg-ember/10 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.1em] text-ember">{error}</p>
            )}
            <button type="submit" data-testid="admin-login-button" disabled={busy} className="flex w-full items-center justify-center gap-2 bg-ember px-6 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-coal-900 transition-opacity disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : "Sign in"}
            </button>
          </form>
        </div>
      ) : (
        <div className="px-5 py-12 md:px-10 md:py-16">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ember">[ Inbox ]</p>
              <h1 className="font-display text-5xl uppercase leading-[0.85] tracking-tight md:text-6xl">
                Quote requests <span className="text-ember">({quotes.length})</span>
              </h1>
            </div>
            <button
              data-testid="admin-refresh-button"
              onClick={loadQuotes}
              className="flex items-center gap-2 border border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-steel transition-colors hover:border-ember hover:text-ember"
            >
              <RefreshCw size={13} className={loadingQuotes ? "animate-spin" : ""} /> Refresh
            </button>
          </div>

          {loadingQuotes && quotes.length === 0 ? (
            <div className="flex justify-center py-24"><Loader2 className="animate-spin text-ember" /></div>
          ) : quotes.length === 0 ? (
            <p data-testid="admin-quotes-empty" className="border border-line bg-coal-800 px-6 py-16 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-steel">
              No quote requests yet
            </p>
          ) : (
            <div data-testid="admin-quotes-table" className="overflow-x-auto border border-line">
              <div className="hidden grid-cols-12 gap-4 border-b border-line bg-coal-800 px-5 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-steel md:grid">
                <span className="col-span-2">Date</span>
                <span className="col-span-2">Name</span>
                <span className="col-span-2">Phone</span>
                <span className="col-span-2">Service</span>
                <span className="col-span-3">Message</span>
                <span className="col-span-1 text-right">Status</span>
              </div>
              {quotes.map((q) => (
                <div key={q.id} data-testid={`admin-quote-row-${q.id}`} className="grid grid-cols-1 gap-3 border-b border-line px-5 py-5 last:border-b-0 md:grid-cols-12 md:gap-4 md:py-4">
                  <span className="font-mono text-[11px] text-steel md:col-span-2">
                    {new Date(q.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                  <span className="text-sm font-medium md:col-span-2">{q.name}</span>
                  <a href={`tel:${q.phone}`} className="font-mono text-[12px] text-white transition-colors hover:text-ember md:col-span-2">{q.phone}</a>
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel md:col-span-2">{q.service}</span>
                  <span className="text-sm leading-relaxed text-steel md:col-span-3">{q.message}</span>
                  <span className="md:col-span-1 md:text-right">
                    <button
                      data-testid={`admin-quote-status-${q.id}`}
                      onClick={() => toggleStatus(q)}
                      className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                        q.status === "new"
                          ? "bg-ember text-coal-900 hover:bg-white"
                          : "border border-line text-steel hover:border-ember hover:text-ember"
                      }`}
                    >
                      {q.status}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
